import React, { useEffect, useState, useMemo } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useSelector, useDispatch } from "react-redux";
import { getAllMeetingTasks } from "../../features/meetings/meetingsSlice";
import {
  updateTaskInState,
  removeTaskFromState,
} from "../../features/meetings/meetingsSlice";
import {
  executeTask,
  deleteTask,
  updateTask,
} from "../../features/tasks/taskSlice";
import { Icons } from "../../constants/icon-library";
import { toast } from "react-toastify";
import TaskList from "../Meetings/TaskList";
import { fetchSettingDetailsByUserId } from "../../features/settings/settingsSlice";
import LoadingComponent from "../../components/common/LoadingComponent";
import { confirmationModal } from "../../constants/confirmation.constants";
import { getParticipants } from "../../features/participant/participantSlice";
import { getUserActivity } from "../../features/user/userSlice";

const TaskLists = () => {
  const dispatch = useDispatch();

  const [selectedTasks, setSelectedTasks] = useState([]);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editedTaskName, setEditedTaskName] = useState("");
  const [editedTaskDescription, setEditedTaskDescription] = useState("");
  const [executingTaskIds, setExecutingTaskIds] = useState(new Set());
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const { meetings, loading } = useSelector((state) => state.meetings);
  const { userSettings } = useSelector((state) => state.setting);
  const { participants: allParticipants = [] } = useSelector(
    (state) => state.participants,
  );

  useEffect(() => {
    dispatch(getAllMeetingTasks());
    dispatch(fetchSettingDetailsByUserId());
    dispatch(getParticipants());
    dispatch(getUserActivity()); //TODO: Remove this after confirmation
  }, [dispatch]);

  const allTasks = useMemo(() => {
    if (!meetings?.length) return [];
    return meetings.flatMap((meeting) => {
      const actions = meeting?.tasks?.actions || [];
      return actions.map((action) => ({
        ...action,
        meetingId: meeting.uuid,
        meetingTitle: meeting.title,
        meetingParticipants: meeting.participants || [],
      }));
    });
  }, [meetings]);

  const filteredTasks = useMemo(() => {
    if (!startDate && !endDate) return allTasks;
    return allTasks.filter((task) => {
      if (!task.createdAt) return true;
      const taskDate = new Date(task.createdAt);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;
      if (start && taskDate < start) return false;
      if (end) {
        end.setHours(23, 59, 59, 999);
        if (taskDate > end) return false;
      }
      return true;
    });
  }, [allTasks, startDate, endDate]);

  const handleTaskUpdate = async (uuid, payload) => {
    const taskItem = allTasks.find((t) => t.uuid === uuid);
    if (!taskItem) return;

    const result = await dispatch(
      updateTask({ uuid, meetingId: taskItem.meetingId, ...payload }),
    );

    if (updateTask.fulfilled.match(result)) {
      dispatch(
        updateTaskInState({
          meetingId: taskItem.meetingId,
          taskUuid: uuid,
          changes: payload,
        }),
      );
      toast.success("Task updated successfully");
    } else {
      toast.error("Failed to update task");
    }
  };

  const handleExecute = async (actionIds) => {
    const matchedActions = allTasks.filter((t) => actionIds.includes(t.uuid));
    setExecutingTaskIds((prev) => {
      const updated = new Set(prev);
      matchedActions.forEach((a) => updated.add(a.uuid));
      return updated;
    });
    setSelectedTasks([]);

    for (const action of matchedActions) {
      const result = await dispatch(
        executeTask({ taskData: action, meetingId: action.meetingId }),
      );

      if (executeTask.fulfilled.match(result) && result.payload?.success) {
        toast.success("Task executed successfully");
        dispatch(
          updateTaskInState({
            meetingId: action.meetingId,
            taskUuid: action.uuid,
            changes: {
              status: "completed",
              actionLogId: result.payload?.task?.actionLogId ?? null,
            },
          }),
        );
      } else {
        toast.error(result.payload?.message || "Failed to execute task");
        dispatch(
          updateTaskInState({
            meetingId: action.meetingId,
            taskUuid: action.uuid,
            changes: {
              status: "failed",
              actionLogId: result.payload?.task?.actionLogId ?? null,
            },
          }),
        );
      }

      setExecutingTaskIds((prev) => {
        const updated = new Set(prev);
        updated.delete(action.uuid);
        return updated;
      });
    }
  };

  const handleDeleteTask = async (taskItem) => {
    if (!taskItem?.meetingId || !taskItem?.uuid) return;
    const result = await confirmationModal(
      "Are you sure?",
      "This will delete it permanently.",
    );

    if (result.isConfirmed) {
      const res = await dispatch(
        deleteTask({ taskId: taskItem.uuid, meetingId: taskItem.meetingId }),
      );

      if (deleteTask.fulfilled.match(res)) {
        dispatch(
          removeTaskFromState({
            meetingId: taskItem.meetingId,
            taskUuid: taskItem.uuid,
          }),
        );
        toast.success("Task removed successfully.");
      } else {
        toast.error("Failed to remove task.");
      }
    }
  };

  const handleSaveEdit = async (taskItem) => {
    const taskName = editedTaskName.trim();
    const description = editedTaskDescription.trim();
    if (!taskName || description.length < 10) return;

    const result = await dispatch(
      updateTask({
        uuid: taskItem.uuid,
        meetingId: taskItem.meetingId,
        status: taskItem.status,
        name: taskName,
        description,
      }),
    );

    if (updateTask.fulfilled.match(result)) {
      dispatch(
        updateTaskInState({
          meetingId: taskItem.meetingId,
          taskUuid: taskItem.uuid,
          changes: { name: taskName, description },
        }),
      );
    } else {
      toast.error("Failed to save task");
    }

    setEditingTaskId(null);
    setEditedTaskName("");
    setEditedTaskDescription("");
  };

  const handleStartEdit = (taskItem) => {
    setEditingTaskId(taskItem.uuid);
    setEditedTaskName(taskItem.name);
    setEditedTaskDescription(taskItem.description);
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
    setEditedTaskName("");
    setEditedTaskDescription("");
  };

  const handleKeyDown = (e, taskItem) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit(taskItem);
    } else if (e.key === "Escape") handleCancelEdit();
  };

  const handleToggleTask = (taskId) => {
    setSelectedTasks((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId],
    );
  };

  const handleToggleAll = () => {
    setSelectedTasks(
      selectedTasks.length === allTasks.length
        ? []
        : allTasks.map((t) => t.uuid),
    );
  };

  if (loading && !meetings?.length) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <LoadingComponent loading={true} size={15} />
      </div>
    );
  }

  return (
    <div
      className="content"
      style={{ maxWidth: "100%", padding: "4px 20px", marginTop: "40px" }}
    >
      <div className="pb-5 pt-3">
        <div className="bg-body-emphasis rounded-3 p-4 border">
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
            <h3 className="mb-0 fw-semibold d-flex align-items-center gap-2 text-body">
              <i className="bi bi-list-check" />
              All Tasks
              <span
                className="badge bg-secondary-subtle text-secondary-emphasis rounded-pill fw-normal"
                style={{ fontSize: 13 }}
              >
                {filteredTasks.length}
              </span>
            </h3>

            <div className="d-flex align-items-center gap-2">
              <div className="d-flex align-items-center gap-1">
                <label className="form-label mb-0 text-muted small">From</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="d-flex align-items-center gap-1">
                <label className="form-label mb-0 text-muted small">To</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                }}
              >
                Clear
              </button>
            </div>
          </div>

          {filteredTasks.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox fs-1 d-block mb-2" />
              <p className="mb-0">No tasks found across any integrations.</p>
            </div>
          ) : (
            <TaskList
              actions={filteredTasks}
              groupByPlatform={false}
              selectedTasks={selectedTasks}
              editingTaskId={editingTaskId}
              editedTaskName={editedTaskName}
              editedTaskDescription={editedTaskDescription}
              onStartEdit={handleStartEdit}
              onCancelEdit={handleCancelEdit}
              onSaveEdit={handleSaveEdit}
              onDeleteTask={handleDeleteTask}
              onKeyDown={handleKeyDown}
              onTaskNameChange={setEditedTaskName}
              onDescriptionChange={setEditedTaskDescription}
              onSend={handleExecute}
              settings={userSettings}
              onToggleTask={handleToggleTask}
              onTogglePlatformTask={handleToggleTask}
              onToggleAll={handleToggleAll}
              handleToggleAll={handleToggleAll}
              participants={allParticipants}
              onTaskUpdate={handleTaskUpdate}
              executingTaskIds={executingTaskIds}
            />
          )}
        </div>
      </div>

      {selectedTasks?.length > 0 && (
        <div className="execute-bar bg-body-emphasis border-top border-translucent shadow-lg">
          <div className="container-fluid">
            <div className="d-flex align-items-center justify-content-end py-3 px-3">
              <button
                className="btn btn-primary btn-sm execute-bar__btn"
                onClick={() => handleExecute(selectedTasks)}
              >
                <i className={`wb ${Icons("resume")} me-1 align-middle`} />
                Execute
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskLists;
